import { JSX } from 'react'

import { Icon } from '@/icons/Icon'
import { EligibilityOption } from '@/collections/Deal/constants'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { createLogger } from '@/lib/logger'
import { formatNumber } from '@/lib/utils'
import type { App, AppCategoryRecommendation, Category, Deal } from '@/payload-types'

const logger = createLogger('Deal Helpers')

type DealTags = {
  new?: boolean
  limited?: boolean
  daysRemaining?: number | null
}

function formatDuration(days: number): { duration: number; timeUnit: string } {
  if (days >= 360) {
    const years = Math.round(days / 360)
    return {
      duration: years,
      timeUnit: years === 1 ? ' year' : ' years',
    }
  } else if (days > 30) {
    const months = Math.round(days / 30)
    return {
      duration: months,
      timeUnit: months === 1 ? ' month' : ' months',
    }
  } else {
    return {
      duration: days,
      timeUnit: days === 1 ? ' day' : ' days',
    }
  }
}

export function formatDeal(deal: Deal) {
  const iconMap: Record<Deal['unitType'], string> = {
    currency: 'stub',
    percent: 'discount-code',
    days: 'ticket',
    other: 'gift-card',
  }

  /*
  // Handle this action on the backend now

  const getShortname = (deal: Deal): React.ReactNode => {
    const product = deal.product ? (deal.product as Product).name : null
    const durationInfo = deal.durationDays ? formatDuration(deal.durationDays) : null

    switch (deal.unitType) {
      case 'currency':
        return (
          <>
            <span>${deal.quantity}</span> off {product}
            {durationInfo && ` (${durationInfo.duration} ${durationInfo.timeUnit})`}
          </>
        )
      case 'percent':
        return (
          <>
            <span>{deal.quantity}%</span> off {product}
            {durationInfo && ` (${durationInfo.duration} ${durationInfo.timeUnit})`}
          </>
        )
      case 'days': {
        if (!deal.durationDays) {
          return null
        }
        const { duration, timeUnit } = formatDuration(deal.durationDays)
        return (
          <>
            Free
            <span>
              {` ${duration}`}
              {timeUnit}
            </span>
            {product && ` of ${product}`}
          </>
        )
      }
      case 'other': {
        return (
          <>
            <span>{deal.name || 'Special offer'}</span>
          </>
        )
      }
      default: {
        return (
          <>
            <span>{deal.name || 'Special offer'}</span>
          </>
        )
      }
    }
  }
  */

  return {
    id: deal.id,
    shortname: deal.name || 'Special offer',
    button: formatDealButton(deal),
    icon: iconMap[deal.unitType],
  }
}

export function formatDealButton(deal: Deal, app?: string): string {
  const a = app ?? undefined
  const forApp = a ? ` for ${a}` : ''
  const ofApp = a ? ` of ${a}` : ''
  const spaceApp = a ? ` ${a}` : ''

  if (deal.product) return `Claim deal${forApp}`

  switch (deal.unitType) {
    case 'currency':
      return deal.isReward
        ? `Get $${formatNumber(deal.quantity ?? 0)}${forApp}`
        : `Get $${formatNumber(deal.quantity ?? 0)} off${spaceApp}`
    case 'percent':
      return `Get ${deal.quantity}% off${spaceApp}`
    case 'days': {
      if (!deal.durationDays) return `Claim free trial${forApp}`
      const { duration, timeUnit } = formatDuration(deal.durationDays)
      return `Get ${duration}${timeUnit} free${ofApp}`
    }
    case 'other':
      return `Claim deal${forApp}`
    default: {
      // Defensive fallback: previously this threw, which an ErrorBoundary would
      // catch and prevent rendering the entire surrounding tree. Real-world
      // cause is usually a stale-cache shape collision (different callers used
      // the same cacheKey with different `select` shapes — see commit `b34c94a52`
      // and `plan/KNOWN_ISSUES.md` for the full incident). Belt-and-suspenders
      // with the shape-suffixed cacheKeys is the proper fix; this just stops
      // the symptom from breaking the UI if a similar collision recurs.
      logger.warn(
        `formatDealButton: unknown unitType "${deal.unitType}" on deal id=${deal.id ?? '?'} — falling back to "Claim Deal"`,
      )
      return `Claim Deal${forApp}`
    }
  }
}

export function formatEligibility(eligibility: EligibilityOption | Deal['eligibility']) {
  const eligibilityMap: Record<
    EligibilityOption,
    { label: string; icon: JSX.Element; terms: string; only?: boolean }
  > = {
    [EligibilityOption.PreSignup]: {
      label: 'new users',
      icon: <Icon name="user-sparkle" />,
      terms: 'Exclusive deal for first-time users of the software',
      only: true,
    },
    [EligibilityOption.FreeUser]: {
      label: 'free users',
      icon: <Icon name="user-update" />,
      terms: 'Special offer for first-time and free-tier users only',
    },
    [EligibilityOption.Everyone]: {
      label: 'everyone',
      icon: <Icon name="user-check" />,
      terms: 'This deal is available for everyone — no restrictions apply',
    },
  }

  return eligibilityMap[eligibility]
}

export function getDealTags(deal: Deal): DealTags {
  const now = Date.now()
  const isNew = new Date(deal.createdAt).getTime() > now - 1000 * 60 * 60 * 24 * 7
  const isLimited = deal.endDate !== null
  const endDateTimestamp = deal.endDate ? new Date(deal.endDate).getTime() : null

  const daysRemaining =
    endDateTimestamp && endDateTimestamp > now
      ? Math.ceil((endDateTimestamp - now) / (1000 * 60 * 60 * 24))
      : null

  const tags: DealTags = {}

  if (daysRemaining !== null && daysRemaining <= 31) {
    tags.daysRemaining = daysRemaining
  } else if (isNew) {
    tags.new = true
  }

  return tags
}

export function filterAndSortDeals(deals: Deal[], selectedCategoryId: Category['id'] | null) {
  const recommendationOrder: Record<Recommendation, number> = {
    [Recommendation.TopPick]: 1,
    [Recommendation.Recommended]: 2,
    [Recommendation.Capable]: 3,
    [Recommendation.NotRecommended]: 4,
  }

  if (selectedCategoryId === null) {
    return [...deals].sort((a, b) => (b.claimed || 0) - (a.claimed || 0))
  }

  const getCategoryId = (doc: AppCategoryRecommendation) =>
    typeof doc.category === 'number' ? doc.category : doc.category?.id

  const filteredDeals = deals.filter((deal) => {
    const docs = (deal.app as App)?.categoryRecommendations?.docs ?? []
    return docs
      .filter((d): d is AppCategoryRecommendation => typeof d !== 'number')
      .some((doc) => getCategoryId(doc) === selectedCategoryId)
  })

  return filteredDeals.sort((a, b) => {
    const aDocsRaw = (a.app as App)?.categoryRecommendations?.docs ?? []
    const bDocsRaw = (b.app as App)?.categoryRecommendations?.docs ?? []
    const aDocs = aDocsRaw.filter(
      (doc): doc is AppCategoryRecommendation => typeof doc !== 'number',
    )
    const bDocs = bDocsRaw.filter(
      (doc): doc is AppCategoryRecommendation => typeof doc !== 'number',
    )

    const aRec = aDocs.find((doc) => getCategoryId(doc) === selectedCategoryId)
    const bRec = bDocs.find((doc) => getCategoryId(doc) === selectedCategoryId)

    if (aRec && bRec) {
      const aPriority = recommendationOrder[aRec.recommendation] ?? Infinity
      const bPriority = recommendationOrder[bRec.recommendation] ?? Infinity
      return aPriority - bPriority
    }

    if (aRec) return -1 // `a` has a recommendation, `b` doesn't
    if (bRec) return 1 // `b` has a recommendation, `a` doesn't

    return (b.claimed || 0) - (a.claimed || 0) // Fallback: Sort by claimed count
  })
}

type DetermineDealsInput = Pick<Deal, 'id' | 'startDate' | 'endDate' | 'order'> & {
  app?: Deal['app']
  featuredOrder?: Deal['featuredOrder']
}

export function determineDealsToDisplay<T extends DetermineDealsInput>(deals: T[]): T[] {
  if (!deals || deals.length === 0) return []

  const now = new Date()

  // First, filter to only active deals based on date restrictions
  const activeDeals = deals.filter((deal) => {
    const hasStart = Boolean(deal.startDate)
    const hasEnd = Boolean(deal.endDate)

    // If no dates are set, consider it always active
    if (!hasStart && !hasEnd) return true

    const startDate = hasStart && deal.startDate ? new Date(deal.startDate) : null
    const endDate = hasEnd && deal.endDate ? new Date(deal.endDate) : null

    // Handle invalid dates
    if (startDate && isNaN(startDate.getTime())) return false
    if (endDate && isNaN(endDate.getTime())) return false

    // Only startDate: active if start has passed
    if (startDate && !endDate) return startDate <= now

    // Only endDate: active if end hasn't passed
    if (!startDate && endDate) return endDate >= now

    // Both dates: active if within range
    return startDate! <= now && endDate! >= now
  })

  // Now handle duplicates among active deals
  const seen = new Set<string>()
  const result: T[] = []

  for (const deal of activeDeals) {
    // Create keys for order and featuredOrder
    // Use 0 as fallback when app isn't populated — treats all deals as same-app for dedup
    const appId = deal.app ? (typeof deal.app === 'number' ? deal.app : deal.app.id) : 0
    const orderKey =
      deal.order !== null && deal.order !== undefined ? `order-${appId}-${deal.order}` : null
    const featuredKey =
      deal.featuredOrder !== null && deal.featuredOrder !== undefined
        ? `featured-${appId}-${deal.featuredOrder}`
        : null

    // Check if we've seen this order or featuredOrder before
    const hasDuplicate = (orderKey && seen.has(orderKey)) || (featuredKey && seen.has(featuredKey))

    if (!hasDuplicate) {
      // First time seeing this order/featuredOrder - add it
      result.push(deal)
      if (orderKey) seen.add(orderKey)
      if (featuredKey) seen.add(featuredKey)
    } else {
      // Found a duplicate - keep the newest (higher ID)
      const existingIndex = result.findIndex((d) => {
        const existingAppId = d.app ? (typeof d.app === 'number' ? d.app : d.app.id) : 0
        return (
          (orderKey && existingAppId === appId && d.order === deal.order) ||
          (featuredKey && existingAppId === appId && d.featuredOrder === deal.featuredOrder)
        )
      })

      if (existingIndex !== -1) {
        const existing = result[existingIndex]
        // A deal is "time-scoped" if it has EITHER a start or end date. A deal
        // with only a start date (no expiry) is still a valid limited-time deal
        // and must take priority over an undated evergreen deal at the same order.
        const dealHasDates = Boolean(deal.startDate || deal.endDate)
        const existingHasDates = Boolean(existing.startDate || existing.endDate)

        // Prefer time-scoped deals over undated ones
        if (dealHasDates && !existingHasDates) {
          result[existingIndex] = deal
        } else if (dealHasDates === existingHasDates && deal.id > existing.id) {
          result[existingIndex] = deal
        }
      }
    }
  }

  return result.sort((a, b) => {
    const aOrder = a.order ?? Infinity
    const bOrder = b.order ?? Infinity
    return aOrder - bOrder
  })
}
